import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import styled from 'styled-components';
import { onAuthStateChanged, User } from 'firebase/auth';
import HomeScreen from './components/HomeScreen';
import SearchScreen from './components/SearchScreen';
import ProfileScreen from './components/ProfileScreen';
import WallpaperScreen from './components/WallpaperScreen';
import HistoryScreen from './components/HistoryScreen';
import LoginScreen from './components/LoginScreen';
import RegisterScreen from './components/RegisterScreen';
import HelpScreen from './components/HelpScreen';
import AboutScreen from './components/AboutScreen';
import PrivacyScreen from './components/PrivacyScreen';
import TermsScreen from './components/TermsScreen';
import Navigation from './components/Navigation';
import Footer from './components/Footer';
import CookieConsent from './components/CookieConsent';
import { userService } from './services/userService';
import { auth } from './services/firebaseConfig';
import { firestoreService } from './services/firestoreService';

const AppContainer = styled.div`
  min-height: 100vh;
  background-color: var(--color-white);
  display: flex;
  flex-direction: column;
`;

const MainContent = styled.main`
  flex: 1;
  padding: 20px;
  max-width: 1000px;
  margin: 0 auto;
  width: 100%;
  
  @media (max-width: 768px) {
    padding: 15px;
  }
`;

const App: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<any>(null);
  const [firebaseUser, setFirebaseUser] = useState<User | null>(null);
  const [showCookieConsent, setShowCookieConsent] = useState(false);

  // Firebase 认证状态监听
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setFirebaseUser(firebaseUser);
      
      if (firebaseUser) {
        // Firebase 用户已登录
        setIsAuthenticated(true);
        
        // 尝试从 Firestore 获取用户详细信息
        try {
          const firestoreUser = await firestoreService.getUser(firebaseUser.uid);
          if (firestoreUser) {
            // 检查 Firebase 邮箱是否与 Firestore 不同（用户可能通过验证链接更新了邮箱）
            const firebaseEmail = firebaseUser.email || '';
            const firestoreEmail = firestoreUser.email || '';
            
            if (firebaseEmail !== firestoreEmail && firebaseEmail) {
              console.log('🔄 Email change detected, updating Firestore:', {
                firebaseEmail,
                firestoreEmail
              });
              
              // 更新 Firestore 中的邮箱
              await firestoreService.updateUser(firebaseUser.uid, {
                email: firebaseEmail,
                emailVerified: firebaseUser.emailVerified,
                updatedAt: new Date().toISOString()
              });
              
              // 更新本地存储
              const currentUser = localStorage.getItem('userInfo');
              if (currentUser) {
                const userData = JSON.parse(currentUser);
                userData.email = firebaseEmail;
                userData.emailVerified = firebaseUser.emailVerified;
                localStorage.setItem('userInfo', JSON.stringify(userData));
              }
              
              // Trigger custom event to notify other components that email has been updated
              window.dispatchEvent(new CustomEvent('emailUpdated', { 
                detail: { 
                  email: firebaseEmail,
                  emailVerified: firebaseUser.emailVerified
                } 
              }));
            }
            
            // 使用 Firestore 中的用户数据，但优先使用 Firebase 的邮箱和验证状态
            setUser({
              id: firestoreUser.id,
              username: firestoreUser.username,
              email: firebaseUser.email || firestoreUser.email, // 优先使用 Firebase 邮箱
              avatar: firestoreUser.avatar,
              provider: firestoreUser.provider,
              emailVerified: firebaseUser.emailVerified, // 使用 Firebase 的验证状态
              createdAt: firestoreUser.createdAt.toDate().toISOString(),
              updatedAt: firestoreUser.updatedAt.toDate().toISOString(),
              lastLoginAt: firestoreUser.lastLoginAt.toDate().toISOString(),
              credits: firestoreUser.credits,
              firebaseUser: firebaseUser
            });
          } else {
            // 如果 Firestore 中没有用户数据，使用 Firebase 用户数据
            setUser({
              id: firebaseUser.uid,
              username: firebaseUser.displayName || 'User',
              email: firebaseUser.email || '',
              avatar: firebaseUser.photoURL || '/uploads/demo-avatar.png',
              provider: 'firebase',
              emailVerified: firebaseUser.emailVerified,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
              lastLoginAt: new Date().toISOString(),
              credits: 5,
              firebaseUser: firebaseUser
            });
          }
        } catch (error) {
          console.error('获取 Firestore 用户信息失败:', error);
          // 如果获取失败，使用 Firebase 用户数据
          setUser({
            id: firebaseUser.uid,
            username: firebaseUser.displayName || 'User',
            email: firebaseUser.email || '',
            avatar: firebaseUser.photoURL || '/uploads/demo-avatar.png',
            provider: 'firebase',
            emailVerified: firebaseUser.emailVerified,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            lastLoginAt: new Date().toISOString(),
            credits: 5,
            firebaseUser: firebaseUser
          });
        }
      } else {
        // Firebase 用户未登录，检查后端认证状态
        try {
          const isLoggedIn = await userService.checkAuthStatus();
          setIsAuthenticated(isLoggedIn);
          
          if (isLoggedIn) {
            const userData = await userService.getCurrentUser();
            setUser(userData);
          } else {
            setUser(null);
          }
        } catch (error) {
          console.error('检查后端认证状态失败:', error);
          setIsAuthenticated(false);
          setUser(null);
        }
      }
      
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // 检查cookie同意状态
  useEffect(() => {
    const cookieConsent = localStorage.getItem('cookieConsent');
    if (!cookieConsent) {
      setShowCookieConsent(true);
    }
  }, []);

  const handleAcceptCookies = () => {
    localStorage.setItem('cookieConsent', 'accepted');
    setShowCookieConsent(false);
  };

  const handleDeclineCookies = () => {
    localStorage.setItem('cookieConsent', 'declined');
    setShowCookieConsent(false);
  };

  const handleLogin = (userData?: any) => {
    setIsAuthenticated(true);
    if (userData) {
      setUser(userData);
    }
  };

  const handleLogout = async () => {
    try {
      // 如果使用 Firebase 登录，先登出 Firebase
      if (firebaseUser) {
        await auth.signOut();
      }
      
      // 登出后端
      await userService.logout();
      
      setIsAuthenticated(false);
      setUser(null);
      setFirebaseUser(null);
    } catch (error) {
      console.error('登出失败:', error);
    }
  };


  if (isLoading) {
    return (
      <AppContainer>
        <MainContent>
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <h2>Loading...</h2>
          </div>
        </MainContent>
      </AppContainer>
    );
  }

  return (
    <AppContainer>
      <Navigation 
        isAuthenticated={isAuthenticated} 
        user={isAuthenticated ? user : undefined} 
        onLogout={handleLogout}
      />
      
      <MainContent>
        <Routes>
          <Route 
            path="/" 
            element={<HomeScreen isAuthenticated={isAuthenticated} />}
          />
          <Route 
            path="/search" 
            element={<SearchScreen isAuthenticated={isAuthenticated} />}
          />
          <Route 
            path="/wallpaper" 
            element={<WallpaperScreen isAuthenticated={isAuthenticated} onLogin={handleLogin} />} 
          />
          <Route 
            path="/history" 
            element={
              isAuthenticated ? 
                <HistoryScreen /> : 
                <Navigate to="/login" replace />
            } 
          />
          <Route 
            path="/profile" 
            element={
              isAuthenticated ? 
                <ProfileScreen user={user} /> : 
                <Navigate to="/login" replace />
            } 
          />
          <Route 
            path="/help" 
            element={<HelpScreen />}
          />
          <Route 
            path="/about" 
            element={<AboutScreen />}
          />
          <Route 
            path="/privacy" 
            element={<PrivacyScreen />}
          />
          <Route 
            path="/terms" 
            element={<TermsScreen />}
          />
          <Route 
            path="/login" 
            element={
              !isAuthenticated ? 
                <LoginScreen onLogin={handleLogin} /> : 
                <Navigate to="/" replace />
            } 
          />
          <Route 
            path="/register" 
            element={<RegisterScreen onLogin={handleLogin} />} 
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </MainContent>
      
      <Footer />
      
      {showCookieConsent && (
        <CookieConsent 
          onAccept={handleAcceptCookies}
          onDecline={handleDeclineCookies}
        />
      )}
    </AppContainer>
  );
};

export default App;
